import { FC, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { browserSupportsWebAuthn, startRegistration } from '@simplewebauthn/browser';
import { Avatar, Button, Col, Popconfirm, Row, Typography } from 'antd';
import { isAxiosError } from 'axios';
import { TbFingerprintScan, TbPlus, TbTrash, TbX } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { PlatformAlert } from '@/shared/components/alerts';
import { EmptyView } from '@/shared/components/layouts/views/EmptySection';
import { BaseSkeleton } from '@/shared/components/skeletons';
import { toast } from '@/shared/components/toasts/PlatformToast';

interface PasskeySummary {
    aaguid?: string;
    createdAt: string;
    credentialBackedUp: boolean;
    credentialDeviceType?: string;
    id: string;
    lastUsedAt?: string;
    name: string;
    transports: string[];
    updatedAt: string;
}

interface PasskeysResponse {
    passkeys: PasskeySummary[];
}

interface PasskeyRegistrationResponse {
    passkey: PasskeySummary;
}

const formatDateTime = (value?: string) => {
    if (!value) {
        return 'Never';
    }

    return new Date(value).toLocaleString([], {
        dateStyle: 'medium',
        timeStyle: 'short',
    });
};

const getPasskeyErrorMessage = (error: unknown) => {
    if (error instanceof Error && error.name === 'NotAllowedError') {
        return 'Passkey setup was canceled or timed out.';
    }

    if (isAxiosError(error) && typeof error.response?.data?.message === 'string') {
        return error.response.data.message;
    }

    return 'Passkey setup failed. Try again from this device.';
};

const Passkeys: FC = (): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [loading, setLoading] = useState(true);
    const [mutating, setMutating] = useState(false);
    const [passkeys, setPasskeys] = useState<PasskeySummary[]>([]);
    const [error, setError] = useState('');
    const passkeysSupported = browserSupportsWebAuthn();

    useEffect(() => {
        let mounted = true;

        void authorizedAxiosInstance
            .get<PasskeysResponse>('/api/auth/account/passkeys')
            .then((response) => {
                if (mounted) {
                    setPasskeys(response.data.passkeys);
                }
            })
            .catch((incomingError: unknown) => {
                captureException(incomingError);

                if (mounted) {
                    setError('Unable to load passkeys.');
                }
            })
            .finally(() => {
                if (mounted) {
                    setLoading(false);
                }
            });

        return () => {
            mounted = false;
        };
    }, []);

    const addPasskey = async () => {
        if (!passkeysSupported) {
            setError('Passkeys are not available in this browser.');

            return;
        }

        try {
            setError('');
            setMutating(true);

            const optionsResponse = await authorizedAxiosInstance.post(
                '/api/auth/account/passkeys/generate-registration-options',
            );
            const registrationResponse = await startRegistration({ optionsJSON: optionsResponse.data });
            const verificationResponse = await authorizedAxiosInstance.post<PasskeyRegistrationResponse>(
                '/api/auth/account/passkeys/verify-registration',
                registrationResponse,
            );

            setPasskeys((currentPasskeys) => [
                verificationResponse.data.passkey,
                ...currentPasskeys.filter((passkey) => passkey.id !== verificationResponse.data.passkey.id),
            ]);
            toast.success('Passkey added');
        } catch (incomingError) {
            captureException(incomingError);
            setError(getPasskeyErrorMessage(incomingError));
        } finally {
            setMutating(false);
        }
    };

    const removePasskey = async (passkeyId: string) => {
        try {
            setError('');
            setMutating(true);

            await authorizedAxiosInstance.delete(`/api/auth/account/passkeys/${passkeyId}`);

            setPasskeys((currentPasskeys) => currentPasskeys.filter((passkey) => passkey.id !== passkeyId));
            toast.success('Passkey removed');
        } catch (incomingError) {
            captureException(incomingError);
            setError('Unable to remove passkey.');
        } finally {
            setMutating(false);
        }
    };

    const renderNewPasskeyButton = () => (
        <Button
            type='dashed'
            icon={<TbPlus size='1rem' style={{ verticalAlign: '-0.15rem' }} />}
            loading={mutating}
            disabled={!passkeysSupported || loading}
            onClick={addPasskey}
        >
            New Passkey
        </Button>
    );

    return (
        <>
            <Typography.Title id='settings-account-security-passkeys' level={4}>
                <TbFingerprintScan size='1rem' style={{ verticalAlign: '-0.15rem', marginRight: '0.25rem' }} />
                Passkeys
            </Typography.Title>
            <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                Passkeys are a secure way to log into Countercyclical.
            </Typography.Paragraph>
            <div
                style={{
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadius,
                    marginBottom: '1rem',
                    overflow: 'hidden',
                }}
            >
                {loading ? (
                    <div style={{ padding: '1rem' }}>
                        <BaseSkeleton active paragraph={{ rows: 1 }} title={false} />
                    </div>
                ) : passkeys.length === 0 ? (
                    <EmptyView
                        description='Register a passkey to sign in securely without a password.'
                        extra={renderNewPasskeyButton()}
                        height='13rem'
                        icon={<TbFingerprintScan size='1.25rem' />}
                        label='No Passkeys Registered'
                    />
                ) : (
                    <>
                        {passkeys.map((passkey, index) => (
                            <Row
                                key={passkey.id}
                                align='top'
                                gutter={[12, 12]}
                                style={{
                                    borderTop: index === 0 ? undefined : `1px solid ${token.colorBorder}`,
                                    padding: '1rem',
                                }}
                            >
                                <Col flex='none'>
                                    <Avatar
                                        icon={
                                            <TbFingerprintScan size='75%' style={{ color: token.colorTextTertiary }} />
                                        }
                                        shape='square'
                                        style={{ border: `1px dashed ${token.colorBorder}`, marginTop: '0.2rem' }}
                                    />
                                </Col>
                                <Col flex='auto'>
                                    <Typography.Text strong>{passkey.name}</Typography.Text>
                                    <div style={{ color: token.colorTextTertiary, fontSize: token.fontSizeSM }}>
                                        Created {formatDateTime(passkey.createdAt)} · Last used{' '}
                                        {formatDateTime(passkey.lastUsedAt)}
                                    </div>
                                    {/* <div style={{ marginTop: '0.375rem' }}>
                                        {passkey.credentialDeviceType && (
                                            <Tag bordered={false}>{passkey.credentialDeviceType}</Tag>
                                        )}
                                        <Tag bordered={false}>
                                            {passkey.credentialBackedUp ? 'Synced' : 'Device-bound'}
                                        </Tag>
                                    </div> */}
                                </Col>
                                <Col flex='none'>
                                    <Popconfirm
                                        title='Remove passkey'
                                        description='This passkey will no longer sign in to your account.'
                                        okText='Remove'
                                        okButtonProps={{ danger: true, loading: mutating }}
                                        onConfirm={() => removePasskey(passkey.id)}
                                        arrow={false}
                                        placement='bottomRight'
                                    >
                                        <Button
                                            danger
                                            type='text'
                                            aria-label='Remove passkey'
                                            icon={<TbTrash size='1rem' style={{ verticalAlign: '-0.15rem' }} />}
                                            disabled={mutating}
                                        />
                                    </Popconfirm>
                                </Col>
                            </Row>
                        ))}
                        <Row
                            align='middle'
                            justify='end'
                            style={{ borderTop: `1px solid ${token.colorBorder}`, padding: '0.75rem 1rem' }}
                        >
                            <Col>{renderNewPasskeyButton()}</Col>
                        </Row>
                    </>
                )}
            </div>
            {!passkeysSupported && (
                <PlatformAlert
                    type='warning'
                    showIcon
                    message='Passkeys are not available in this browser.'
                    style={{ marginBottom: '1rem' }}
                />
            )}
            {error && (
                <PlatformAlert
                    closable
                    type='error'
                    closeIcon={<TbX size='1rem' />}
                    showIcon
                    message={error}
                    onClose={() => setError('')}
                    style={{ marginBottom: '1rem' }}
                />
            )}
        </>
    );
};

export default Passkeys;
